import {_serializeSingle, _serializeList} from '../core';
import type {ServerData} from '../core';
import type {DomainOf} from '../../../../types/domain';
import type {WithDocumentLabels} from '../_extras';

/** 스키마 밖에서 조립되는 관계 필드 — 서버 응답 계약의 일부. */
type ParticipantBffExtras = WithDocumentLabels;

export function serializeDocumentLabel(data: ServerData) {
    return _serializeSingle("documentLabel", data);
}

export function serializeDocumentLabelList(data: ServerData) {
    return _serializeList("documentLabel", data);
}

export function serializeDocumentDepartment(data: ServerData) {
    return _serializeSingle("documentDepartment", data);
}

export function serializeDocumentDepartmentList(data: ServerData) {
    return _serializeList("documentDepartment", data);
}

export function serializeDocumentRequesterBff(data: ServerData) {
    if (!data) return null;
    const requester = {
        ..._serializeSingle("documentRequester", data),
        ...serializeDocumentParticipant(data.documentParticipant),
    };
    return requester;
}

export function serializeDocumentApprover(data: ServerData) {
    return _serializeSingle("documentApprover", data);
}

export function serializeDocumentApproverList(data: ServerData) {
    return _serializeList("documentApprover", data);
}

export function serializeDocumentApproverBff(data: ServerData) {
    if (!data) return null;
    const approver: Record<string, unknown> = {
        ..._serializeSingle("documentApprover", data),
        ...serializeDocumentParticipant(data.documentParticipant),
    };
    // 대리 처리자
    if (data.documentParticipantActor) {
        approver.documentParticipantActor = serializeDocumentParticipant(data.documentParticipantActor);
    }
    return approver;
}

export function serializeDocumentApproverBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => serializeDocumentApproverBff(item));
}

export function serializeDocumentSealApproverBff(data: ServerData) {
    if (!data) return null;
    const sealApprover: Record<string, unknown> = {
        ..._serializeSingle("documentSealApprover", data),
        ...serializeDocumentParticipant(data.documentParticipant),
    };
    // 대리 처리자
    if (data.documentParticipantActor) {
        sealApprover.documentParticipantActor = serializeDocumentParticipant(data.documentParticipantActor);
    }
    return sealApprover;
}

export function serializeDocumentSealManagerBff(data: ServerData) {
    if (!data) return null;
    const sealManager: Record<string, unknown> = {
        ..._serializeSingle("documentSealManager", data),
        ...serializeDocumentParticipant(data.documentParticipant),
    };
    // 대리 처리자
    if (data.documentParticipantActor) {
        sealManager.documentParticipantActor = serializeDocumentParticipant(data.documentParticipantActor);
    }
    return sealManager;
}

export function serializeDocumentSealManagerBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => serializeDocumentSealManagerBff(item));
}

export function serializeDocumentSealApproverBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => serializeDocumentSealApproverBff(item));
}

// 라벨(직위·직책)은 참여자 객체 안에 들어옴
//
// 부서는 배열이 아니라 departmentId·departmentName 등 평탄 필드로 옴
// 문서 참여자는 작성 시점 소속 부서 1개만 스냅샷되므로 배열이 없음
export function serializeDocumentParticipant(data: ServerData) {
    if (!data) return null;
    const normalized = {
        ...data,
        number: data.memberNumber ?? data.number,
        name: data.memberName ?? data.name,
        email: data.memberEmail ?? data.email,
        phone: data.memberPhone ?? data.phone,
        cellPhone: data.memberCellPhone ?? data.cellPhone,
        address: data.memberAddress ?? data.address,
        birthDate: data.memberBirthDate ?? data.birthDate,
        joinDate: data.memberJoinDate ?? data.joinDate,
        quitDate: data.memberQuitDate ?? data.quitDate,
        profileUrl: data.memberProfileUrl ?? data.profileUrl,
        role: data.memberRole ?? data.role,
        state: data.memberState ?? data.state,
        externalStateId: data.memberExternalStateId ?? data.externalStateId,
        externalStateName: data.memberExternalStateName ?? data.externalStateName,
        externalUpdatedDate: data.memberExternalUpdatedDate ?? data.externalUpdatedDate,
    };
    const participant: DomainOf<"documentParticipant"> & ParticipantBffExtras = _serializeSingle("documentParticipant", normalized);

    if (data.documentLabels) {
        participant.documentLabels = serializeDocumentLabelList(data.documentLabels);
    }
    return participant;
}

export function serializeDocumentParticipantList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => serializeDocumentParticipant(item));
}

export function serializeDocumentRequester(data: ServerData) {
    return _serializeSingle("documentRequester", data);
}

export function serializeDocumentRevoker(data: ServerData) {
    return _serializeSingle("documentRevoker", data);
}

export function serializeDocumentRevokerBff(data: ServerData) {
    if (!data) return null;
    const revoker = {
        ..._serializeSingle("documentRevoker", data),
        ...serializeDocumentParticipant(data.documentParticipantActor),
    };
    return revoker;
}
